import React, { useCallback } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { DocumentStatus, FileType } from '@alucio/aws-beacon-amplify/src/models';
import { DNABox, DNACard, DNACheckbox, Iffy, DNAText, DNATextProps, DNAButton, util } from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import {
  ActionCallbacks,
  CustomLabelDisplay,
  DNAChip,
  DNACustomFieldChipList,
  DNADocumentStatusChip,
  DNADocumentThumbnail,
  DNADocumentContextMenu,
  DNAPopover,
  useCurrentPage,
  useDNADocumentActions,
} from 'src/components/DNA';
import { Variant, DocumentORM } from 'src/types/types';
import { useAppSettings } from 'src/state/context/AppSettings';
import { useDocumentSearchV2Context } from 'src/hooks/useDocumentSearchV2';

const S = StyleSheet.create({
  searchContentWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  excerptContent: {
    overflowWrap: 'break-word' as const,
    whiteSpace: 'pre-wrap' as const,
    maxWidth: '100%',
  },
  searchCheckbox: {
    margin: 6,
  },
  card: {
    padding: 16,
  },
  unavailableContent: {
    opacity: 0.4,
  },
  checked: {
    backgroundColor: colors['color-brand2-5'],
  },
  documentSearchResultRowContentContainer: {
    marginLeft: 16,
  },
  documentSearchResultBadgesContainer: {
    marginRight: 8,
  },
  popoverText: {
    color: colors['color-text-white'],
  },
})

interface DocumentSearchResultRowProps {
  documentORM: DocumentORM
  selectEnabled?: boolean,
  onCheck?: (() => void) | undefined,
  checked?: boolean,
  isDropdown?: boolean,
  onPress?: ((pageNumber?: number) => void) | undefined,
  actionCallbacks?: ActionCallbacks,
}

export const RenderExcerpt: React.FC<{
  pageNumber?: number;
  textProps: DNATextProps;
  text: string | null | undefined;
}> = (props) => {
  const { pageNumber, textProps, text } = props;

  // Function to parse the HTML and safely render both text and bold elements
  // Created to prevent XSS attacks by rendering only specific tags
  const renderParsedHTMLWithBoldTags = useCallback((htmlString: string): JSX.Element[] => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlString, 'text/html');

      // Helper function to process nodes recursively
      const processNode = (node: Node, keyPrefix: string): JSX.Element | string | JSX.Element[] => {
        if (node.nodeType === Node.TEXT_NODE) {
          const textContent = node.textContent || '';
          const parts = textContent.split('\n');
          return parts.map((part, partIndex) => (
            <>
              <span key={`${keyPrefix}-${partIndex}`}>{part}</span>
              {partIndex < parts.length - 1 && <br key={`${keyPrefix}-${partIndex}-br`} />}
            </>
          ));
        }

        if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as HTMLElement;
          const tagName = element.tagName.toLowerCase();

          // Process all child nodes recursively
          const children = Array.from(element.childNodes).map((child, idx) =>
            processNode(child, `${keyPrefix}-${idx}`),
          );

          switch (tagName) {
            case 'b':
            case 'strong':
              return <strong key={keyPrefix}>{children}</strong>;
            case 'i':
            case 'em':
              return <i key={keyPrefix}>{children}</i>;
            case 'u':
              return <u key={keyPrefix}>{children}</u>;
            case 'br':
              return <br key={keyPrefix} />;
            default:
              // Wrap children in a fragment to return a single element
              return <>{children}</>;
          }
        }

        return <span key={keyPrefix} />;
      };

      // Wrap the mapped nodes in fragments to ensure we always return Elements
      return Array.from(doc.body.childNodes).map((node, index) => (
        <React.Fragment key={`root-${index}`}>
          {processNode(node, `root-${index}`)}
        </React.Fragment>
      ));
    } catch (error) {
      console.error('Failed to parse HTML:', error);
      return [<span key="error">Error parsing content</span>];
    }
  }, []);

  // Function to clean the text and add page number if available
  const renderContent = useCallback(
    (text: string | null | undefined, pageNumber?: number): JSX.Element => {
      const cleanedText = (text || '').trim().replace(/\n\s+/g, ' ');
      const contentWithPageNumber = cleanedText && pageNumber
        ? `Slide ${pageNumber} - ${cleanedText}`
        : cleanedText;

      return (
        <div style={S.excerptContent}>
          {renderParsedHTMLWithBoldTags(contentWithPageNumber)}
        </div>
      );
    },
    [renderParsedHTMLWithBoldTags], // Dependencies are correctly set
  );

  return (
    <DNABox appearance="row">
      <DNAText {...textProps} numberOfLines={textProps.numberOfLines} testID={textProps.testID}>
        {renderContent(text, pageNumber)}
      </DNAText>
    </DNABox>
  );
};

const currentPageQuery = { exact: false }

const DocumentSearchResultRow: React.FC<DocumentSearchResultRowProps> = React.memo((props) => {
  const { documentORM, selectEnabled, checked, onCheck, isDropdown, onPress, actionCallbacks } = props;
  const { isOnline, deviceMode } = useAppSettings();
  const unavailableContent =
    !isOnline &&
    (!documentORM.relations.version.cachedDocumentVersionORM?.meta.assets.isContentCached ||
      documentORM.model.type === FileType.WEB)

  const { documentIdSnippetMap } = useDocumentSearchV2Context();

  const snippetMap = documentIdSnippetMap;
  const snippet = snippetMap.get(documentORM.model.id);
  const route = useCurrentPage(currentPageQuery)
  const isPublisherRoute = !!route?.configOptions?.modules?.includes('publisher')
  const variant = isPublisherRoute ? Variant.publisher : Variant.MSL;

  const documentActions = useDNADocumentActions();

  const showSnippet = !!snippet?.documentExcerpt;
  const isDesktop = deviceMode === 'desktop';
  const showBookmarkButton = !unavailableContent && !isPublisherRoute && !isDropdown;
  const showContextMenu = !isDropdown;
  const isRowClickable = !!onPress && !unavailableContent;

  const handleRowClick = () => {
    if (!isRowClickable) {
      return;
    }

    onPress?.(snippet?.pageNumber);
  }

  const customThumbnail = (documentORM: DocumentORM, pageNumber?: number) => {
    if (!pageNumber) return documentORM.meta.assets.thumbnailKey;
    return documentORM.meta.assets.thumbnailKey?.replace(/(\d+)_small_thumb\.png$/, `${pageNumber}_small_thumb.png`);
  }

  const titleProps: DNATextProps = {
    b1: true,
    numberOfLines: 1,
    testID: 'drop-down-results-title',
  }

  const snippetProps: DNATextProps = {
    numberOfLines: isDropdown ? 3 : 0,
    status: 'subtle',
    testID: 'drop-down-results-snippet',
  }

  const customThumbnailKey = customThumbnail(documentORM, snippet?.pageNumber);
  const documentVersion = documentORM.relations.version.latestDocumentVersionORM;
  const scheduledPublish = documentVersion.meta.schedule.publish.scheduledAt;
  const isDraft = documentORM.meta.hasUnpublishedVersion &&
    documentORM.model.status === DocumentStatus.PUBLISHED;

  return (
    <DNACard
      appearance="flat"
      interactive={isRowClickable ? 'pressable' : undefined}
      style={util.mergeStyles(
        undefined,
        S.card,
        [S.unavailableContent, unavailableContent],
        [S.checked, checked],
      )}
      onPress={handleRowClick}
      disabled={unavailableContent}
    >
      <View style={S.searchContentWrapper}>
        <DNABox alignY="center">
          <Iffy is={selectEnabled}>
            <DNACheckbox
              context="altBg"
              style={S.searchCheckbox}
              checked={checked}
              onChange={onCheck}
            />
          </Iffy>
          <Iffy is={showBookmarkButton}>
            <DNAButton
              appearance="ghost"
              status={documentORM.meta?.bookmark?.isBookmarked ? 'primary' : 'subtle'}
              size={isDesktop ? 'md' : 'xl'}
              iconLeft={documentORM.meta?.bookmark?.isBookmarked ? 'star-fill' : 'star'}
              onPress={documentActions.bookmark(documentORM)}
            />
          </Iffy>
          <Iffy is={showContextMenu}>
            <DNADocumentContextMenu
              documentORM={documentORM}
              variant={variant}
              pageNumber={snippet?.pageNumber}
              actionCallbacks={actionCallbacks}
            >
              <DNAButton
                appearance="ghost"
                status="subtle"
                size={isDesktop ? 'md' : 'xl'}
                iconLeft="dots-three-vertical-bold"
              />
            </DNADocumentContextMenu>
          </Iffy>
          <DNADocumentThumbnail.Hover
            documentVersionORM={
              (!isOnline && documentORM.relations.version.cachedDocumentVersionORM) ||
              documentORM.relations.version.latestPublishedDocumentVersionORM ||
              documentORM.relations.version.latestDocumentVersionORM
            }
            width={160}
            height={87}
            unavailableContent={unavailableContent}
            onPress={isPublisherRoute
              ? documentActions.publisherPreview(documentORM, snippet?.pageNumber)
              : handleRowClick}
            showProcessing
            customThumbnailKey={customThumbnailKey}
            pageNumber={snippet?.pageNumber}
          />
        </DNABox>
        <Pressable
          onPress={handleRowClick}
          disabled={!isRowClickable}
          style={S.documentSearchResultRowContentContainer}
        >
          <DNABox appearance="col" fill spacing="sm">
            {snippet?.title
              ? <RenderExcerpt
                  textProps={titleProps}
                  text={snippet.title.text}
              />
              : <DNAText b1 testID="drop-down-results-title">
                {documentORM.relations.version.latestUsableDocumentVersionORM.model.title}
              </DNAText>}
            {showSnippet &&
              <RenderExcerpt
                pageNumber={snippet.pageNumber}
                textProps={snippetProps}
                text={snippet.documentExcerpt.text}
              />
            }
            <DNABox>
              <DNABox spacing="sm" style={S.documentSearchResultBadgesContainer}>
                <DNACustomFieldChipList
                  documentVersionORM={documentORM.relations.version.latestUsableDocumentVersionORM}
                />
                <Iffy is={isPublisherRoute}>
                  {documentORM.meta.hasUnpublishedVersion ? (
                    <DNABox spacing="xs" key={documentORM.model.id}>
                      <DNAPopover>
                        <DNAPopover.Anchor>
                          <DNADocumentStatusChip
                            documentStatus={documentORM.model.status}
                            isScheduled={!!scheduledPublish}
                            isDraft={isDraft}
                          />
                        </DNAPopover.Anchor>
                        <DNAPopover.Content offset={-60}>
                          <DNAText numberOfLines={1} style={S.popoverText}>
                            New version in progress
                          </DNAText>
                        </DNAPopover.Content>
                      </DNAPopover>
                    </DNABox>
                  ) : (
                    <DNABox spacing="xs" key={documentORM.model.id}>
                      <DNADocumentStatusChip
                        documentStatus={documentORM.model.status}
                      />
                    </DNABox>
                  )}
                </Iffy>
                <Iffy is={unavailableContent}>
                  <DNAChip.Beacon
                    testID="content-not-available"
                    variant="CONTENT_UNAVAILABLE"
                  />
                </Iffy>
              </DNABox>
              <CustomLabelDisplay documentORM={documentORM} />
            </DNABox>
          </DNABox>
        </Pressable>
      </View>
    </DNACard>
  )
})

export default DocumentSearchResultRow;
